import React from "react";
import Seo from "../components/Seo";
import Loading from "../components/Loading";
import "../css/Documentation.css";
import {CHART_HEIGHT, ChartTitle, formatMemory, MemoryChart, parseMemoryRow, rssSummary, rssToMb} from "../components/MemoryChart";
import {LegendMarker, markerFor, parseRunConditions, selectCurrentBrokerRuns, SeriesMarker, SERVER_COLORS, KNOWN_BROKERS, sortServersByName} from "../components/BrokerBenchmark";
import connectionsUrl from "../xmq_test_results/Connections.txt";


// "Server:" also names the host in the newer files' preamble ("Server:   AWS c5n.4xlarge..."),
// so only a known broker starts a result block. Without this the host line becomes a broker
// with no data points and an entry in the legend.

// Meta keys that are per-server but not interesting as their own table column
// because they duplicate the test title (e.g. "Scenario: 500K Connection Test").
const SKIPPED_META_KEYS = ["Scenario"];

function parseConnectionsResult(text)
{
    const conditions = {};
    let note = null;
    const servers = [];
    let current = null;

    for (const raw of text.split("\n")) {
        const line = raw.trim();
        if (line === "" || line.startsWith("Conditions") || line.startsWith("Interval") || /^─+$/.test(line))
            continue;

        const noteMatch = line.match(/^Note:\s*(.+)$/);
        if (noteMatch) { note = noteMatch[1]; continue; }

        const serverMatch = line.match(/^Server:\s*(.+)$/);
        if (serverMatch) {
            const name = serverMatch[1].trim();
            if (KNOWN_BROKERS.test(name)) {
                current = {name, meta: {}, dataPoints: [], memoryPoints: []};
                servers.push(current);
            } else {
                current = null;
            }
            continue;
        }

        // Count, latency and rate, then the broker's RSS: one column in the files of 2026-10-07,
        // two - average and median - from 2026-10-16, so the trailing columns repeat. A single
        // optional column here left the Average row unread and the last two table columns empty.
        const avgMatch = line.match(/^Average\s+(\d+)\s+(\d+)us(?:\s+\d+)?(?:\s+[\d.]+\s*(?:Mb|Gb))*$/i);
        if (avgMatch && current) {
            current.averageCount = parseInt(avgMatch[1], 10);
            current.averageLatency = parseInt(avgMatch[2], 10);
            continue;
        }

        const memory = current && parseMemoryRow(line);
        if (memory) {
            current.memoryPoints.push(memory);
            continue;
        }

        // The fifth column, when there is one, is the broker's RSS in the interval.
        const rowMatch = line.match(/^(\d+)ms\s+(\d+)\s+(\d+)us(?:\s+\d+)?(?:\s+([\d.]+)\s*(Mb|Gb))?$/i);
        if (rowMatch && current) {
            const interval = parseInt(rowMatch[1], 10);
            current.dataPoints.push({
                interval,
                count: parseInt(rowMatch[2], 10),
                latency: parseInt(rowMatch[3], 10),
            });
            if (rowMatch[4])
                current.memoryPoints.push({interval, rssMb: rssToMb(rowMatch[4], rowMatch[5])});
            continue;
        }

        if (current && line === "Percentiles") {
            current.readingPercentiles = true;
            continue;
        }
        if (current && current.readingPercentiles) {
            const percentile = line.match(/^(p\d+(?:\.\d+)?|max)\s+(\d+)(us|ms)$/);
            if (percentile) {
                current.percentiles = current.percentiles || {};
                current.percentiles[percentile[1]] = parseInt(percentile[2], 10) * (percentile[3] === "ms" ? 1000 : 1);
                continue;
            }
            current.readingPercentiles = false;
        }

        const medianMatch = line.match(/^Median\s+(\d+)us(?:\s+.*)?$/);
        if (current && medianMatch) {
            current.medianLatency = parseInt(medianMatch[1], 10);
            continue;
        }

        const kvMatch = line.match(/^([A-Za-z ]+?):\s+(.+)$/);
        if (kvMatch) {
            const key = kvMatch[1].trim();
            const value = kvMatch[2].trim();
            if (current)
                current.meta[key] = value;
            else
                conditions[key] = value;
        }
    }

    // The conditions line is the preamble's bare line before "Date:" - the one that says the run is an
    // MQTT 5 one. The key/value loop above cannot see it, since it has no key.
    return {conditions, note, conditionsLine: parseRunConditions(text), servers: selectCurrentBrokerRuns(servers)};
}

function formatLatency(us)
{
    if (us >= 1_000_000)
        return `${(us / 1_000_000).toFixed(2)}s`;
    if (us >= 1000)
        return `${(us / 1000).toFixed(1)}ms`;
    return `${us}us`;
}

function niceTicks(min, max, count)
{
    if (min === max)
        return [min];
    const step = (max - min) / (count - 1);
    return Array.from({length: count}, (_, i) => Math.round(min + step * i));
}

/**
 * Each row of the connection test covers an interval and says how many clients connected in it;
 * plotted against the running total, a point sits on the number of connections actually made by the
 * end of its interval - so every broker ends on all of them, however long it took. Plotted against
 * time, a slow broker ran past the last connection and a broker with no stragglers stopped short of it.
 *
 * A trailing interval of a few stragglers is folded into the one before it: its latency is that of
 * one or two connections, and its memory belongs to the end of the run.
 */
function atConnectionsMade(server)
{
    const points = server.dataPoints.map((p) => ({...p}));
    const memory = server.memoryPoints.map((m) => ({...m}));
    const tail = points[points.length - 1];
    if (tail && tail.count < 10 && points.length > 1) {
        points.pop();
        points[points.length - 1].count += tail.count;
        const tailMemory = memory.find((m) => m.interval === tail.interval);
        const before = memory.find((m) => m.interval === points[points.length - 1].interval);
        if (tailMemory && before)
            before.rssMb = Math.max(before.rssMb, tailMemory.rssMb);
    }
    let made = 0;
    const madeBy = new Map();
    for (const p of points) {
        made += p.count;
        madeBy.set(p.interval, made);
    }
    return {
        ...server,
        dataPoints: points.map((p) => ({...p, interval: madeBy.get(p.interval)})),
        memoryPoints: memory.filter((m) => madeBy.has(m.interval)).map((m) => ({...m, interval: madeBy.get(m.interval)})),
    };
}

function LatencyChart({servers, xFormat, width = 760, height = CHART_HEIGHT})
{
    const margin = {top: 16, right: 16, bottom: 40, left: 96};
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    const chartServers = servers;

    const allPoints = chartServers.flatMap((s) => s.dataPoints);
    if (allPoints.length === 0)
        return null;

    const xMax = Math.max(...allPoints.map((p) => p.interval));
    const yMinRaw = Math.min(...allPoints.map((p) => p.latency));
    const yMaxRaw = Math.max(...allPoints.map((p) => p.latency));
    const useLog = yMinRaw > 0 && yMaxRaw / yMinRaw > 50;
    const yMin = useLog ? yMinRaw : 0;
    const yMax = yMaxRaw;

    const xScale = (x) => margin.left + (xMax === 0 ? 0 : x / xMax) * innerW;
    const yScale = (y) => {
        if (useLog) {
            const t = (Math.log10(y) - Math.log10(yMin)) / (Math.log10(yMax) - Math.log10(yMin) || 1);
            return margin.top + innerH - t * innerH;
        }
        const t = yMax === yMin ? 0 : (y - yMin) / (yMax - yMin);
        return margin.top + innerH - t * innerH;
    };

    const xTicks = niceTicks(0, xMax, 6);
    const yTicks = useLog
        ? (() => {
            const lo = Math.floor(Math.log10(yMin));
            const hi = Math.ceil(Math.log10(yMax));
            const ticks = [];
            for (let p = lo; p <= hi; p++)
                ticks.push(Math.pow(10, p));
            return ticks;
        })()
        : niceTicks(yMin, yMax, 6);

    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{width: "100%", maxWidth: width, height: "auto"}}>
            <text transform={`translate(16, ${margin.top + innerH / 2}) rotate(-90)`}
                  textAnchor="middle" fontSize="11" fill="#777">
                Latency
            </text>
            {yTicks.map((t, idx) => (
                <g key={`y${idx}`}>
                    <line x1={margin.left} x2={width - margin.right} y1={yScale(t)} y2={yScale(t)} stroke="#eee"/>
                    <text x={margin.left - 8} y={yScale(t)} textAnchor="end" dominantBaseline="middle" fontSize="11">
                        {formatLatency(Math.round(t))}
                    </text>
                </g>
            ))}
            {xTicks.map((t, idx) => (
                <g key={`x${idx}`}>
                    <text x={xScale(t)} y={height - margin.bottom + 16} textAnchor="middle" fontSize="11">
                        {xFormat(t)}
                    </text>
                </g>
            ))}
            <line x1={margin.left} x2={width - margin.right} y1={height - margin.bottom} y2={height - margin.bottom}
                  stroke="#999"/>
            <line x1={margin.left} x2={margin.left} y1={margin.top} y2={height - margin.bottom} stroke="#999"/>

            {chartServers.map((s) => (
                <polyline
                    key={s.name}
                    fill="none"
                    stroke={SERVER_COLORS[s.name] || "#333"}
                    strokeWidth="2"
                    points={s.dataPoints
                        .map((p) => `${xScale(p.interval)},${yScale(Math.max(p.latency, yMin || 1))}`)
                        .join(" ")}
                />
            ))}

            {chartServers.map((s, i) => s.dataPoints.map((p, j) => (
                <SeriesMarker key={`${s.name}-${j}`} shape={markerFor(s.name)}
                              cx={xScale(p.interval)} cy={yScale(Math.max(p.latency, yMin || 1))}
                              fill={SERVER_COLORS[s.name] || "#333"}/>
            )))}
        </svg>
    );
}

function ConnectionsTest({title, test})
{
    const [nameAscending, setNameAscending] = React.useState(true);
    if (!test)
        return null;

    const {conditions, note, conditionsLine} = test;
    const ordered = sortServersByName(test.servers, nameAscending)
        .map((server) => ({server, measured: atConnectionsMade(server)}));
    const servers = ordered.map(({measured}) => measured);
    const xFormat = (connections) => `${Math.round(connections / 1000)}K`;
    // "Port" said only which port of ours the broker happened to listen on - an internal detail of
    // the test rig, and nothing a reader can use. "Max CPU" was how the first runs recorded it;
    // every run since samples the whole scenario and records "CPU Load" as mean and peak, so the
    // column that carried a number now carries the pair.
    // Max RAM is shown for every broker on every test page, with a dash where a run did not
    // measure it.
    const extraColumns = [...["Version", "CPU Load"].filter((key) =>
        servers.some((s) => s.meta[key] !== undefined && !SKIPPED_META_KEYS.includes(key))
    ), "Max RAM"];

    return (
        <div style={{marginBottom: 40}}>
            <h3>{title}</h3>
            <ul>
                {Object.entries(conditions).map(([key, value]) => (
                    <li key={key}><b>{key}:</b> {value}</li>
                ))}
            </ul>
            {note && <p><i>{note}</i></p>}
            {conditionsLine && <p style={{fontSize: "0.9em", color: "#666"}}>{conditionsLine}</p>}

            <table cellPadding="4" cellSpacing="4">
                <thead>
                <tr>
                    <th style={{cursor: "pointer", userSelect: "none"}}
                        onClick={() => setNameAscending((value) => !value)}
                        title="Sort by broker name" aria-sort={nameAscending ? "ascending" : "descending"}>
                        Broker {nameAscending ? "▲" : "▼"}
                    </th>
                    {extraColumns.map((c) => <th key={c}>{c}</th>)}
                    <th>Avg RAM</th>
                    <th>Median RAM</th>
                    <th>Avg Connections</th>
                    <th>Avg Latency</th>
                    <th>Median</th>
                    <th>p99</th>
                    <th>p99.9</th>
                </tr>
                </thead>
                <tbody>
                {ordered.map(({server, measured: s}) => {
                    // Over the intervals as recorded, before the tail is folded for the charts.
                    const ram = rssSummary(server.memoryPoints);
                    return (
                    <tr key={s.name}>
                        <td>
                            <span style={{
                                display: "inline-block", width: 10, height: 10,
                                backgroundColor: SERVER_COLORS[s.name] || "#333", marginRight: 6
                            }}/>
                            {s.name}
                        </td>
                        {extraColumns.map((c) => <td key={c}>{s.meta[c] || "-"}</td>)}
                        <td>{ram ? formatMemory(ram.average) : "-"}</td>
                        <td>{ram ? formatMemory(ram.median) : "-"}</td>
                        <td>{s.averageCount?.toLocaleString()}</td>
                        <td>{formatLatency(s.averageLatency)}</td>
                        <td>{s.medianLatency === undefined ? "-" : formatLatency(s.medianLatency)}</td>
                        <td>{s.percentiles?.p99 === undefined ? "—" : formatLatency(s.percentiles.p99)}</td>
                        <td>{s.percentiles?.["p99.9"] === undefined ? "—" : formatLatency(s.percentiles["p99.9"])}</td>
                    </tr>
                    );
                })}
                </tbody>
            </table>

            <div style={{display: "flex", gap: 16, margin: "12px 0 8px"}}>
                {servers.map((s, i) => (
                    <div key={s.name} style={{display: "flex", alignItems: "center", gap: 4}}>
                        <LegendMarker shape={markerFor(s.name)}
                                      color={SERVER_COLORS[s.name] || "#333"}/>
                        <span>{s.name}</span>
                    </div>
                ))}
            </div>
            <ChartTitle>Connection latency, depending on the number of connections</ChartTitle>
            <LatencyChart servers={servers} xFormat={xFormat}/>
            <ChartTitle>Broker memory (RSS), depending on the number of connections</ChartTitle>
            <MemoryChart series={servers.map((s, i) => ({key: s.name, label: s.name, color: SERVER_COLORS[s.name] || "#333",
                                                       shape: markerFor(s.name), points: s.memoryPoints}))}
                         xFormat={xFormat} xLabel="connections"
                         xTicks={niceTicks(0, Math.max(...servers.flatMap((s) => s.dataPoints.map((p) => p.interval))), 6)}/>
        </div>
    );
}

export default class XMQ_tests_connections extends React.Component
{
    state = {test: null, error: null};

    componentDidMount()
    {
        fetch(connectionsUrl)
            .then((r) => r.text())
            .then((text) => this.setState({test: parseConnectionsResult(text)}))
            .catch((err) => this.setState({error: String(err)}));
    }

    render()
    {
        const {test, error} = this.state;
        return <div key="tests-connections" className="XMQ" style={{textAlign: "left", padding: 8}}>
            <Seo title="MQTT Performance Tests — Concurrent Connections"
                 description="MQTT performance tests of concurrent client connections: XMQ holds 500,000 simultaneous MQTT connections, measured against other MQTT servers."
                 keywords="MQTT performance tests, fast MQTT server, MQTT benchmark, XMQ"
                 path="/xmq_tests_connections"/>
            <h1>MQTT Performance Tests: Concurrent Connections</h1>
            <h3>Connections</h3>
            <p>
                Connection tests measure how many client connections a broker can accept and how
                connect latency behaves as the number of concurrently connected clients grows.
                Each scenario connects clients in batches and records, per interval, how many
                connections completed and their average latency.
            </p>
            <p>
                XMQ 1.0.2 and FlashMQ 1.27.2 were re-measured on 2 November 2026 on the same
                c5n.4xlarge host (16 vCPU, 40 GB) as the other brokers here. Percentiles are
                shown where the run recorded them; a dash means its result file has no percentile
                section.
            </p>

            {error && <p>Failed to load test results: {error}</p>}
            {!test && !error && <Loading text="Loading results…"/>}
            <ConnectionsTest title="500K Connections" test={test}/>
        </div>;
    }
}
